import { Injectable } from '@angular/core';
import { LoopTypeWriterOptions, TypeWriterOptions } from 'models-common';
import { concat, from, interval, of, repeat, Subject } from 'rxjs';
import {
  concatMap,
  delay,
  ignoreElements,
  map,
  take,
  tap,
} from 'rxjs/operators';

interface TypeParams {
  word: string;
  speed: number;
  backwards?: boolean;
}

const WRITE_SPEED_DEFAULT = 50;
const WRITE_DELAY_DEFAULT = 0;
const WAIT_AFTER_TYPED_DEFAULT = 2000;
const WAIT_AFTER_DELETED_DEFAULT = 1000;

@Injectable()
export class TypewriterService {
  public readonly complete$ = new Subject<void>();

  private typeWord({ word, speed, backwards }: TypeParams) {
    return interval(speed).pipe(
      map((x) => {
        return backwards
          ? word.substring(0, word.length - x)
          : word.substring(0, x + 1);
      }),
      take(backwards ? word.length + 1 : word.length),
    );
  }

  private typeEffect(word: string, options: Required<TypeWriterOptions>) {
    return concat(
      of('').pipe(delay(options.writeDelay), ignoreElements()),
      this.typeWord({ word, speed: options.writeSpeed }),
    );
  }

  private loopTypeEffect(
    word: string,
    options: Required<LoopTypeWriterOptions>,
  ) {
    return concat(
      of('').pipe(delay(options.writeDelay), ignoreElements()),
      this.typeWord({ word, speed: options.writeSpeed }),
      of('').pipe(delay(options.waitAfterTyped), ignoreElements()),
      this.typeWord({ word, speed: options.writeDeleteSpeed, backwards: true }),
      of('').pipe(delay(options.waitAfterDeleted), ignoreElements()),
    );
  }

  public typewriterEffect(titles: string[], options?: TypeWriterOptions) {
    return from(titles).pipe(
      concatMap((title) =>
        this.typeEffect(title, {
          writeDelay: options?.writeDelay ?? WRITE_DELAY_DEFAULT,
          writeSpeed: options?.writeSpeed ?? WRITE_SPEED_DEFAULT,
        }).pipe(
          tap((value) => {
            if (value === title) {
              this.complete$.next();
            }
          }),
        ),
      ),
    );
  }

  public loopWriterEffect(titles: string[], options?: LoopTypeWriterOptions) {
    return from(titles).pipe(
      concatMap((title) =>
        this.loopTypeEffect(title, {
          writeDelay: options?.writeDelay ?? WRITE_DELAY_DEFAULT,
          writeSpeed: options?.writeSpeed ?? WRITE_SPEED_DEFAULT,
          writeDeleteSpeed: options?.writeDeleteSpeed ?? WRITE_SPEED_DEFAULT,
          waitAfterDeleted:
            options?.waitAfterDeleted ?? WAIT_AFTER_DELETED_DEFAULT,
          waitAfterTyped: options?.waitAfterTyped ?? WAIT_AFTER_TYPED_DEFAULT,
        }),
      ),
      repeat(),
    );
  }
}
